// CIN-14084: give first-party server-side (SSR) API calls a distinctive, per-client User-Agent so
// the CloudFront WAF can positively identify our own traffic — both as a family (rule
// `cineamo:first-party`, regex `(?i)^cineamo-`) and per client + CD build (visible in the WAF logs
// / drill-down). The default outbound UA from a Next.js server-side fetch is `node`, which is
// indistinguishable from any other anonymous server-side caller.
//
// Server-side only: browsers forbid scripts from overriding User-Agent (it is a forbidden header
// per the fetch spec), and client-side requests already carry the real browser UA.
//
// This is identification, NOT authentication — a User-Agent is trivially spoofable. It is
// nonetheless what excludes SSR traffic from Bot Control: the `cineamo:first-party` label derived
// from this UA is one of the two arms of that rule's ScopeDownStatement (see
// iac/templates/main/waf.template.yaml). CIN-14171's plan to use a client_credentials Bearer for
// that purpose was abandoned; the token is no longer the whitelist. Security is unaffected — a
// spoofed Cineamo-* UA still faces IP reputation, known-bad-inputs, OWASP and RateLimitPerIp.

type ApiClientIdentity = {
    /** Short client slug, e.g. 'Web', 'CinemaWebsite'. Rendered as the `Cineamo-<client>` token. */
    client: string;
    /** Optional explicit build id; defaults to the CD commit SHA (see resolveBuild). */
    build?: string;
};

// CIN-14379: stored on globalThis (Symbol.for = process-wide registry) rather than module scope —
// the bundler can instantiate this module once per chunk (instrumentation chunk vs route chunks),
// and a module-scoped slot written by the instrumentation copy is invisible to the route copies.
// The per-project CINEAMO_CLIENT_NAME env var (Vercel project settings) is the primary identity
// source; setApiClient() is the in-repo belt-and-braces half.
const IDENTITY_KEY = Symbol.for('cineamo.api/client-identity');

type IdentityGlobal = { [IDENTITY_KEY]?: ApiClientIdentity };

/**
 * Register the running app's identity for the outbound SSR User-Agent. Call once at server startup
 * (e.g. beside setAuth() in an app's auth.ts, or in instrumentation.ts for apps without NextAuth).
 */
export function setApiClient(next: ApiClientIdentity): void {
    (globalThis as IdentityGlobal)[IDENTITY_KEY] = next;
}

function getIdentity(): ApiClientIdentity | undefined {
    return (globalThis as IdentityGlobal)[IDENTITY_KEY];
}

function resolveBuild(explicit?: string): string {
    const raw =
        explicit ??
        process.env.VERCEL_GIT_COMMIT_SHA ?? // set by Vercel CD for every deploy
        process.env.NEXT_PUBLIC_BUILD_ID ??
        process.env.npm_package_version ??
        'dev';
    return raw.slice(0, 12);
}

function resolveClient(): string {
    return getIdentity()?.client ?? process.env.CINEAMO_CLIENT_NAME ?? process.env.NEXT_PUBLIC_CLIENT_NAME ?? 'SSR';
}

/** The full outbound User-Agent, e.g. `Cineamo-Web/2b95f0e7b0c1 (+https://cineamo.com)`. */
export function getApiUserAgent(): string {
    return `Cineamo-${resolveClient()}/${resolveBuild(getIdentity()?.build)} (+https://cineamo.com)`;
}

/**
 * Header map tagging a request as first-party SSR traffic. Empty in the browser, where the
 * User-Agent header cannot (and must not) be overridden. Spread it into a request's headers
 * before caller-supplied headers so an explicit override still wins.
 */
export function getSsrUserAgentHeader(): Record<string, string> {
    return typeof window === 'undefined' ? { 'User-Agent': getApiUserAgent() } : {};
}
